import { useEffect, useMemo, useState } from "react";

import {
    IikoModifierSetTypeDto,
    ModifierSetDto,
    ModifierSetTypeDto,
} from "services/modifierSets/restModels";
import { ModifierSetRequestDto } from "services/modifierSets/restRequests";
import { useExternalProductOptions } from "shared/hooks/useExternalProductOptions";
import { hasLocalizedValue, trimLocalizedMap } from "shared/lib/localizedMap";

import { NO_IIKO_SET_TYPE } from "features/ModifierSets/constants";
import { toCount, toModifierDrafts } from "features/ModifierSets/lib/modifierSetForm";
import {
    ExternalOptionItem,
    ExternalProductOptionsContext,
    ModifierDraft,
} from "features/ModifierSets/types";

interface UseModifierSetFormParams {
    open: boolean;
    modifierSet: ModifierSetDto | null;
    locale: string;
    saving: boolean;
    externalIntegrationEnabled?: boolean;
    externalProductOptionsContext?: ExternalProductOptionsContext | null;
}

// Состояние формы набора целиком: поля, список вариантов и всё, что выводится из данных
// внешнего меню. Диалогу остаётся разметка.
export const useModifierSetForm = ({
    open,
    modifierSet,
    locale,
    saving,
    externalIntegrationEnabled,
    externalProductOptionsContext,
}: UseModifierSetFormParams) => {
    const [activeLang, setActiveLang] = useState<string>(locale);
    const [name, setName] = useState<Record<string, string>>({});
    const [type, setType] = useState<ModifierSetTypeDto>(ModifierSetTypeDto.ChooseOne);
    const [isRequired, setIsRequired] = useState<boolean>(false);
    const [minCount, setMinCount] = useState<string>("0");
    const [maxCount, setMaxCount] = useState<string>("0");
    const [iikoType, setIikoType] = useState<string>(NO_IIKO_SET_TYPE);
    const [isCommonCompoundModifier, setIsCommonCompoundModifier] = useState<boolean>(false);
    const [externalId, setExternalId] = useState<string>("");
    const [modifiers, setModifiers] = useState<ModifierDraft[]>([]);

    const [editedModifierIndex, setEditedModifierIndex] = useState<number | null>(null);
    const [isModifierDialogOpen, setIsModifierDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!open) return;

        setActiveLang(locale);
        setName(modifierSet ? { ...modifierSet.name } : {});
        setType(modifierSet ? modifierSet.type : ModifierSetTypeDto.ChooseOne);
        setIsRequired(modifierSet ? modifierSet.isRequired : false);
        setMinCount(String(modifierSet ? modifierSet.minCountTotalChosen : 0));
        setMaxCount(String(modifierSet ? modifierSet.maxCountTotalChosen : 0));
        setIikoType(modifierSet?.externalSettings?.type || NO_IIKO_SET_TYPE);
        setIsCommonCompoundModifier(
            modifierSet?.externalSettings?.isCommonCompoundModifier || false
        );
        setExternalId(modifierSet?.externalId || "");
        setModifiers(toModifierDrafts(modifierSet));
        setEditedModifierIndex(null);
        setIsModifierDialogOpen(false);
    }, [open, modifierSet, locale]);

    const isGroupModifiers = iikoType === IikoModifierSetTypeDto.GroupModifiers;

    /*
    * Блок внешнего меню показываем только заведениям с подключённой интеграцией.
    * Уже привязанный набор показываем всегда — иначе привязку нельзя было бы снять
    * после отключения интеграции.
    * */
    const showExternalBinding = !!externalIntegrationEnabled || !!modifierSet?.externalSettings;

    const { options: externalOptions, loading: externalOptionsLoading } =
        useExternalProductOptions(
            externalProductOptionsContext ? externalProductOptionsContext.menuId : null,
            externalProductOptionsContext ? externalProductOptionsContext.externalProductId : null,
            open && showExternalBinding && iikoType !== NO_IIKO_SET_TYPE
        );

    // Варианты внешнего меню, доступные модификаторам: у размеров и простых модификаторов —
    // плоский список товара, у групповых — состав выбранной группы.
    const modifierExternalOptions = useMemo<ExternalOptionItem[]>(() => {
        if (!externalOptions) return [];

        if (iikoType === IikoModifierSetTypeDto.PriceSize) {
            return externalOptions.sizes.map((size) => ({
                id: size.id,
                name: size.name,
                price: size.price,
            }));
        }

        if (iikoType === IikoModifierSetTypeDto.Modifiers) {
            return externalOptions.modifiers.map((modifier) => ({
                id: modifier.id,
                name: modifier.name,
                price: modifier.price,
            }));
        }

        if (isGroupModifiers && externalId) {
            const group = externalOptions.groupModifiers.find((item) => item.id === externalId);

            return (group?.items || []).map((item) => ({
                id: item.id,
                name: item.name,
                price: item.price,
            }));
        }

        return [];
    }, [externalOptions, iikoType, isGroupModifiers, externalId]);

    // Группы внешнего меню доступны только когда набор правят из позиции с привязкой.
    const groupOptions = useMemo<ExternalOptionItem[]>(
        () =>
            externalProductOptionsContext
                ? (externalOptions?.groupModifiers || []).map((group) => ({
                      id: group.id,
                      name: group.name,
                      price: null,
                  }))
                : [],
        [externalOptions, externalProductOptionsContext]
    );

    const countsValid =
        type !== ModifierSetTypeDto.ChooseMany || toCount(maxCount) >= toCount(minCount);
    const canSubmit = hasLocalizedValue(name) && countsValid && !saving;

    const openModifierDialog = (index: number | null) => {
        setEditedModifierIndex(index);
        setIsModifierDialogOpen(true);
    };

    const closeModifierDialog = () => {
        setIsModifierDialogOpen(false);
        setEditedModifierIndex(null);
    };

    const submitModifier = (modifier: ModifierDraft) => {
        setModifiers((prev) =>
            editedModifierIndex === null
                ? [...prev, modifier]
                : prev.map((item, index) => (index === editedModifierIndex ? modifier : item))
        );
        closeModifierDialog();
    };

    // Удаляем без подтверждения: список правится локально, отмена диалога вернёт всё назад.
    const removeModifier = (index: number) =>
        setModifiers((prev) => prev.filter((_item, i) => i !== index));

    // Привязка осмысленна только для выбранного типа набора — при смене сбрасываем.
    const changeIikoType = (value: string) => {
        setIikoType(value);
        setExternalId("");
    };

    const buildRequest = (): ModifierSetRequestDto => ({
        type,
        name: trimLocalizedMap(name),
        modifiers: modifiers.map((modifier) => ({
            id: modifier.id,
            name: trimLocalizedMap(modifier.name),
            sellPrice: modifier.sellPrice,
            isInStopList: modifier.isInStopList,
            externalId: modifier.externalId,
        })),
        isRequired,
        minCountTotalChosen: toCount(minCount),
        maxCountTotalChosen: toCount(maxCount),
        externalId: externalId.trim() || null,
        externalSettings:
            iikoType === NO_IIKO_SET_TYPE
                ? null
                : {
                      type: iikoType as IikoModifierSetTypeDto,
                      isCommonCompoundModifier,
                  },
    });

    return {
        activeLang,
        setActiveLang,
        name,
        setName,
        type,
        setType,
        isRequired,
        setIsRequired,
        minCount,
        setMinCount,
        maxCount,
        setMaxCount,
        iikoType,
        changeIikoType,
        isCommonCompoundModifier,
        setIsCommonCompoundModifier,
        externalId,
        setExternalId,
        modifiers,
        editedModifier:
            editedModifierIndex === null ? null : modifiers[editedModifierIndex] || null,
        isModifierDialogOpen,
        openModifierDialog,
        closeModifierDialog,
        submitModifier,
        removeModifier,
        countsValid,
        canSubmit,
        showExternalBinding,
        modifierExternalOptions,
        groupOptions,
        externalOptionsLoading,
        buildRequest,
    };
};
